// Cover for a blog index card.
//
// Three modes, in priority order:
//   1. `coverArt: "companions"` — the four-Companion lineup used on the
//      "Life is better with Companions" hero, rendered as the cover.
//   2. `imageSrc` — `<img>` with `object-fit: cover` and the supplied
//      object-position so the cover focuses on the right part of the
//      composition.
//   3. fallback — striped tint placeholder with an optional mascot peek.
import type { CSSProperties } from "react";

import type { BlogPost, BlogTint } from "./posts";
import { Mascot, type MascotName } from "../../components/Mascot";

type Palette = { bg: string; stripe: string };

const PALETTES: Record<BlogTint, Palette> = {
  indigo: { bg: "#EEF2FF", stripe: "#E0E7FF" },
  sage: { bg: "#D1FAE5", stripe: "#A7F3D0" },
  plum: { bg: "#FCE7F3", stripe: "#FBCFE8" },
  amber: { bg: "#FEF3C7", stripe: "#FDE68A" },
};

const COMPANIONS: Array<{
  name: MascotName;
  accent: string;
  tint: string;
}> = [
  { name: "Otto", accent: "#6366F1", tint: "#E0E7FF" },
  { name: "Summi", accent: "#EC4899", tint: "#FCE7F3" },
  { name: "Quill", accent: "#F59E0B", tint: "#FEF3C7" },
  { name: "Sage", accent: "#10B981", tint: "#D1FAE5" },
];

type BlogCoverProps = {
  tint?: BlogTint;
  mascot?: MascotName;
  /** Cover height in pixels. */
  height?: number;
  /** Optional cover image. */
  imageSrc?: string;
  /** Optional cover image for narrow viewports (≤ 640px). */
  imageSrcMobile?: string;
  /** object-position for the cover image (e.g. "35% 50%"). */
  imagePosition?: string;
  /** Alt text for the cover image. */
  imageAlt?: string;
  /** Special cover treatments. See BlogPost["coverArt"]. */
  coverArt?: BlogPost["coverArt"];
  className?: string;
};

export function BlogCover({
  tint = "indigo",
  mascot,
  height = 240,
  imageSrc,
  imageSrcMobile,
  imagePosition = "center",
  imageAlt = "",
  coverArt,
  className = "",
}: BlogCoverProps) {
  if (coverArt === "companions") {
    return <CompanionsCover height={height} className={className} />;
  }

  if (imageSrc) {
    return (
      <div
        className={`relative w-full overflow-hidden rounded-[14px] bg-grindigo-50 ${className}`}
        style={{ height }}
      >
        <picture>
          {imageSrcMobile && (
            <source media="(max-width: 640px)" srcSet={imageSrcMobile} />
          )}
          <img
            src={imageSrc}
            alt={imageAlt}
            className="block h-full w-full object-cover"
            style={{ objectPosition: imagePosition }}
          />
        </picture>
      </div>
    );
  }

  const palette = PALETTES[tint];
  const placeholderStyle: CSSProperties = {
    height,
    backgroundImage: `repeating-linear-gradient(135deg, ${palette.bg} 0 14px, ${palette.stripe} 14px 16px)`,
  };
  const mascotSize = Math.round(height * 0.7);

  return (
    <div
      className={`relative flex w-full items-end overflow-hidden rounded-[14px] ${className}`}
      style={placeholderStyle}
    >
      {mascot && (
        <div
          aria-hidden="true"
          className="pointer-events-none absolute -right-2.5 -bottom-3.5 rotate-[8deg] [filter:drop-shadow(0_12px_22px_rgba(99,102,241,0.25))]"
        >
          <Mascot name={mascot} size={mascotSize} full />
        </div>
      )}
    </div>
  );
}

// Four-Companion lineup cover — mirrors the "Life is better" hero treatment.
// Each Companion sits in a tinted ring on an indigo-gradient backdrop.
function CompanionsCover({
  height,
  className,
}: {
  height: number;
  className: string;
}) {
  // Cap the per-mascot circle so even a tall cover doesn't make them huge.
  const ring = Math.min(96, Math.round(height * 0.55));

  return (
    <div
      className={`relative flex w-full items-center justify-center overflow-hidden rounded-[14px] border border-indigo-200 bg-[linear-gradient(135deg,#EEF2FF_0%,#E0E7FF_100%)] ${className}`}
      style={{ height }}
    >
      <div className="flex flex-wrap items-center justify-center gap-[clamp(8px,2vw,20px)] px-4">
        {COMPANIONS.map((c) => (
          <div
            key={c.name}
            style={
              {
                "--accent": c.accent,
                "--tint": c.tint,
                width: `${ring}px`,
                height: `${ring}px`,
              } as CSSProperties
            }
            className="grid shrink-0 place-items-center overflow-hidden rounded-full border-[3px] border-white bg-[var(--tint)] shadow-[0_12px_24px_-10px_var(--accent)]"
          >
            <Mascot name={c.name} size={ring} />
          </div>
        ))}
      </div>
    </div>
  );
}
